import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Radio,
  RadioGroup,
  Stack,
  Badge,
  Divider,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
} from '@chakra-ui/react';
import { CreditCardForm } from './CreditCardForm';
import { stripeService } from 'services/stripeService';
import { useAuth } from 'contexts/AuthContext';
import {
  StripeSubscriptionPlan,
  CreateSubscriptionRequest,
  SubscriptionDiscountPreview,
} from 'types/stripe';
import { StripeProvider } from './StripeProvider';
import { getStripePublishableKey } from 'utils/stripe';
import FirebaseAnalyticsService from 'services/firebaseAnalyticsService';

interface SubscriptionFormProps {
  onSuccess?: (subscription: any) => void;
  /** Pre-fill promo field (e.g. from marketing links) */
  initialPromotionCode?: string;
}

export const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
  onSuccess,
  initialPromotionCode = '',
}) => {
  const { user, token } = useAuth();
  const toast = useToast();
  const [plans, setPlans] = useState<StripeSubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<
    string | null
  >(null);
  const [setupIntentLoading, setSetupIntentLoading] = useState(false);
  const [promotionCode, setPromotionCode] = useState(initialPromotionCode);
  const [promoPreviewsByPlanId, setPromoPreviewsByPlanId] = useState<
    Record<string, SubscriptionDiscountPreview>
  >({});
  const [promoPreviewLoading, setPromoPreviewLoading] = useState(false);
  const [promoPreviewError, setPromoPreviewError] = useState<string | null>(
    null,
  );
  const promoPreviewSeq = useRef(0);
  const setupIntentRequestSeq = useRef(0);

  const selectedPlanPromoPreview =
    selectedPlan && promoPreviewsByPlanId[selectedPlan]
      ? promoPreviewsByPlanId[selectedPlan]
      : null;

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoadingPlan(true);
        const fetchedPlans = await stripeService.getSubscriptionPlans(token);

        setPlans(fetchedPlans);
        if (fetchedPlans.length > 0) {
          setSelectedPlan(fetchedPlans[0].id);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        setError('Failed to load subscription plans');
      } finally {
        setLoadingPlan(false);
      }
    };

    fetchPlans();
  }, [token]);

  const createSetupIntent = useCallback(async () => {
    const seq = ++setupIntentRequestSeq.current;
    setSetupIntentLoading(true);
    try {
      const response = await stripeService.createSetupIntent(token);
      if (setupIntentRequestSeq.current !== seq) {
        return;
      }
      setSetupIntentClientSecret(response.clientSecret);
    } catch (error) {
      console.error('Error creating setup intent:', error);
      if (setupIntentRequestSeq.current === seq) {
        setError('Failed to initialize payment form');
      }
    } finally {
      if (setupIntentRequestSeq.current === seq) {
        setSetupIntentLoading(false);
      }
    }
  }, [token]);

  const handlePlanSelection = (planId: string) => {
    setSelectedPlan(planId);
    setSetupIntentClientSecret(null);
    void createSetupIntent();
  };

  useEffect(() => {
    const trimmed = promotionCode.trim();
    if (!trimmed || plans.length === 0) {
      setPromoPreviewsByPlanId({});
      setPromoPreviewError(null);
      setPromoPreviewLoading(false);
      return;
    }

    const controller = new AbortController();
    const seq = ++promoPreviewSeq.current;
    const debounceMs = 450;

    const timeoutId = window.setTimeout(async () => {
      setPromoPreviewLoading(true);
      setPromoPreviewError(null);
      try {
        const previews = await Promise.all(
          plans.map((plan) =>
            stripeService.previewSubscriptionDiscount(
              { planId: plan.id, promotionCode: trimmed },
              token,
              controller.signal,
            ),
          ),
        );
        if (promoPreviewSeq.current !== seq || controller.signal.aborted) {
          return;
        }
        const byId: Record<string, SubscriptionDiscountPreview> = {};
        plans.forEach((plan, i) => {
          byId[plan.id] = previews[i];
        });
        setPromoPreviewsByPlanId(byId);
      } catch (e: unknown) {
        const err = e as {
          code?: string;
          name?: string;
          response?: { data?: { message?: string } };
          message?: string;
        };
        if (
          controller.signal.aborted ||
          err.code === 'ERR_CANCELED' ||
          err.name === 'CanceledError'
        ) {
          return;
        }
        if (promoPreviewSeq.current !== seq) {
          return;
        }
        setPromoPreviewsByPlanId({});
        setPromoPreviewError('Could not verify this promotion code');
      } finally {
        if (promoPreviewSeq.current === seq && !controller.signal.aborted) {
          setPromoPreviewLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [promotionCode, plans, token]);

  const handlePaymentMethodAdded = async (paymentMethodId: string) => {
    if (!selectedPlan) {
      toast({
        title: 'Error',
        description: 'Please select a subscription plan',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    FirebaseAnalyticsService.trackFeatureUsage(
      'Billing',
      'create_subscription_started',
      {
        plan_id: selectedPlan,
        has_promo: Boolean(promotionCode.trim()),
      },
    );

    try {
      const request: CreateSubscriptionRequest = {
        planId: selectedPlan,
        paymentMethodId,
        customerEmail: user?.email || '',
        customerName: user?.displayName || '',
      };

      const trimmedPromo = promotionCode.trim();
      if (trimmedPromo) {
        request.promotionCode = trimmedPromo;
      }

      const response = await stripeService.createSubscription(request, token);

      FirebaseAnalyticsService.trackFeatureUsage(
        'Billing',
        'create_subscription_success',
        {
          plan_id: selectedPlan,
          has_promo: Boolean(trimmedPromo),
        },
      );
      onSuccess?.(response.subscription);
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      FirebaseAnalyticsService.trackFeatureUsage(
        'Billing',
        'create_subscription_failed',
        {
          plan_id: selectedPlan,
          has_promo: Boolean(promotionCode.trim()),
        },
      );
      setError(
        error.response?.data?.message || 'Failed to create subscription',
      );
      toast({
        title: 'Error',
        description:
          error.response?.data?.message || 'Failed to create subscription',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // setup intent in useEffect
  useEffect(() => {
    createSetupIntent();
  }, [createSetupIntent]);

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(price / 100); // Assuming price is in cents
  };

  if (loadingPlan) {
    return (
      <VStack spacing={6} align="stretch">
        <Spinner />
      </VStack>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertTitle>Error!</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const promoEntered = Boolean(promotionCode.trim());

  return (
    <VStack spacing={6} align="stretch">
      <FormControl>
        <FormLabel fontWeight="semibold">Promo code (optional)</FormLabel>
        <HStack align="center" spacing={3}>
          <Input
            flex={1}
            placeholder="Enter a promotion code"
            value={promotionCode}
            onChange={(e) => setPromotionCode(e.target.value)}
            autoComplete="off"
          />
          {promoPreviewLoading ? <Spinner size="sm" color="brand.500" /> : null}
        </HStack>
        {promoPreviewError ? (
          <FormHelperText color="red.500">{promoPreviewError}</FormHelperText>
        ) : null}
        {selectedPlanPromoPreview && !promoPreviewLoading ? (
          <Box
            mt={3}
            p={3}
            borderRadius="md"
            bg="green.50"
            borderWidth="1px"
            borderColor="green.100"
          >
            <Text fontSize="sm" fontWeight="semibold" color="green.800" mb={1}>
              {selectedPlanPromoPreview.discountSummary} — applied to all plans
              below
            </Text>
            <HStack spacing={3} align="baseline" flexWrap="wrap">
              {selectedPlanPromoPreview.subtotalCents >
              selectedPlanPromoPreview.totalCents ? (
                <Text
                  fontSize="md"
                  color="gray.600"
                  textDecoration="line-through"
                >
                  {formatPrice(
                    selectedPlanPromoPreview.subtotalCents,
                    selectedPlanPromoPreview.currency,
                  )}
                </Text>
              ) : null}
              <Text fontSize="lg" fontWeight="bold" color="green.700">
                {formatPrice(
                  selectedPlanPromoPreview.totalCents,
                  selectedPlanPromoPreview.currency,
                )}{' '}
                <Text as="span" fontSize="sm" fontWeight="normal">
                  due this period for your selected plan
                </Text>
              </Text>
            </HStack>
          </Box>
        ) : null}
      </FormControl>

      <Box>
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          Choose Your Plan
        </Text>

        <RadioGroup value={selectedPlan} onChange={handlePlanSelection}>
          <Stack spacing={4}>
            {plans.map((plan) => {
              const planPreview = promoPreviewsByPlanId[plan.id];
              const showDiscountOnCard =
                promoEntered &&
                planPreview &&
                !promoPreviewLoading &&
                !promoPreviewError;
              const showPriceSpinner =
                promoEntered && promoPreviewLoading && !promoPreviewError;

              return (
                <Card key={plan.id} variant="outline">
                  <CardBody>
                    <HStack justify="space-between" align="center">
                      <Box flex={1}>
                        <HStack align="center" justify="flex-start">
                          <Radio value={plan.id} />
                          <Text fontWeight="bold">{plan.name}</Text>
                          <Badge colorScheme="brand" variant="subtle">
                            {plan.interval}
                          </Badge>
                        </HStack>
                        <Text color="gray.600" mb={2}>
                          {plan.description}
                        </Text>
                        <VStack align="flex-start" spacing={1}>
                          {plan.features.map((feature, index) => (
                            <Text key={index} fontSize="sm" color="gray.500">
                              • {feature}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                      <Box textAlign="right" minW="7rem">
                        {showPriceSpinner ? (
                          <Spinner size="sm" color="brand.500" ml="auto" />
                        ) : showDiscountOnCard ? (
                          <VStack align="flex-end" spacing={0}>
                            {planPreview.subtotalCents >
                            planPreview.totalCents ? (
                              <Text
                                fontSize="sm"
                                color="gray.500"
                                textDecoration="line-through"
                              >
                                {formatPrice(
                                  planPreview.subtotalCents,
                                  planPreview.currency,
                                )}
                              </Text>
                            ) : null}
                            <Text
                              fontSize="2xl"
                              fontWeight="bold"
                              color="brand.500"
                            >
                              {formatPrice(
                                planPreview.totalCents,
                                planPreview.currency,
                              )}
                            </Text>
                          </VStack>
                        ) : (
                          <Text
                            fontSize="2xl"
                            fontWeight="bold"
                            color="brand.500"
                          >
                            {formatPrice(plan.price, plan.currency)}
                          </Text>
                        )}
                      </Box>
                    </HStack>
                  </CardBody>
                </Card>
              );
            })}
          </Stack>
        </RadioGroup>
      </Box>

      <Divider />
      <Box>
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          Payment Information
        </Text>
        {setupIntentLoading || !setupIntentClientSecret ? (
          <Spinner />
        ) : (
          <StripeProvider
            key={setupIntentClientSecret}
            publishableKey={getStripePublishableKey()}
            clientSecret={setupIntentClientSecret}
          >
            <CreditCardForm
              onSubmit={handlePaymentMethodAdded}
              isLoading={isLoading}
              buttonText="Complete Subscription"
              customerEmail={user?.email}
              customerName={user?.displayName}
              clientSecret={setupIntentClientSecret}
            />
          </StripeProvider>
        )}
      </Box>
    </VStack>
  );
};
